import { Divider, Stack, Stepper, Text, Title, Tooltip, useMantineTheme } from '@mantine/core'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { ImageCrop } from '@/components/MediaWrappers/ImageCrop'
import TransactionSummary from '@/components/Transfer/TransactionSummary'
import { NATIVE_CHAIN_TICKER, STUDIO_BOT } from '@/constants/blockchain'
import { useErrorMessages } from '@/constants/content'
import L1SpacesTemplate from '@/contracts/SpacesTemplate'
import { ContractCall, useChainFunctions, useChainValues } from '@/hooks/useChain'
import useEstimatedFee from '@/hooks/useEstimatedFee'
import { l1NativeWagmiConfig } from '@/plugins/auth/config'
import {
  convertBytesToUnit,
  convertImageToBytes,
  ImmutableDraft,
  ItemMetadata,
  networkOptions,
  post,
  processBalance,
  ProcessedBalance,
  truncateString,
} from '@/utils/utils'

import RootButton from '../../Buttons/RootButton'
import ErrorMessage from '../../ErrorMessage'
import Loading from '../../Loading'
import { jsonSize } from '../CreateItem'
import { ArtItem, SpaceLasersItem } from '../Templates/utils'

interface CheckoutProps {
  contractAddress: string
  title: string
  previewImage: string
  prevStep: () => void
  nextStep: () => void
  imageSize: number | undefined
  itemJson: ImmutableDraft
  metadata: SpaceLasersItem | ArtItem
  deleteDraft: () => void
}

// Used in gas estimate
const defaultCid = 'bafybeidagvw2ykizo3uk5nhpzhh6xfw2guu4c453luie44h2pc7jqlgsoi'

const Checkout: React.FC<CheckoutProps> = ({
  contractAddress,
  title,
  previewImage,
  prevStep,
  nextStep,
  imageSize,
  itemJson,
  metadata,
  deleteDraft,
}) => {
  const theme = useMantineTheme()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const errorMessages = useErrorMessages()

  // State Variables
  const [itemMetadata, setItemMetadata] = useState<ItemMetadata>()
  const [mintPrice, setMintPrice] = useState<ProcessedBalance | undefined>(undefined)
  const contractCalls: ContractCall[] | undefined = useMemo(() => {
    if (!contractAddress || !mintPrice) return undefined
    return [
      {
        args: {
          address: contractAddress as `0x${string}`,
          abi: L1SpacesTemplate,
          functionName: 'mint',
          args: [defaultCid],
          value: mintPrice.value,
        },
      },
    ]
  }, [contractAddress, mintPrice])
  const { estimatedFee } = useEstimatedFee(networkOptions.l1, contractCalls)
  const totalPrice = useMemo(
    () =>
      mintPrice !== undefined ? parseFloat(mintPrice.fullDisplay) + estimatedFee : estimatedFee,
    [mintPrice, estimatedFee]
  )

  const { readContract, writeContract } = useChainFunctions(l1NativeWagmiConfig)
  const { balance } = useChainValues(l1NativeWagmiConfig.id)

  // Loading
  const [loading, setLoading] = useState<boolean>(false)
  const [loadingPrice, setLoadingPrice] = useState<boolean>(false)

  // Error Handling
  const [errorImageUpload, setErrorImageUpload] = useState<string | JSX.Element>('')
  const [errorMetadataUpload, setErrorMetadataUpload] = useState<string | JSX.Element>('')
  const [errorMintItem, setErrorMintItem] = useState<string | JSX.Element>('')

  // Stepper
  const [activeStep, setActiveStep] = useState<number>(0)
  const handleStepClick = useCallback((step: number) => {
    if (step === 0) {
      prevStep()
    } else if (step === 1) {
      nextStep()
    }
  }, [])

  // Get price and fee estimate for item mint
  const getItemMintPrice = useCallback(async () => {
    try {
      setLoadingPrice(true)
      const data = await readContract({
        address: contractAddress as `0x${string}`,
        abi: L1SpacesTemplate,
        functionName: 'price',
      })
      const price = processBalance(data as bigint)
      setMintPrice(price)
      setLoadingPrice(false)
    } catch (err: any) {
      if (err.message.includes('Failed to fetch')) {
        setErrorMintItem(errorMessages.studioBotConnection)
      } else {
        setErrorMintItem(err.message)
      }
    }
  }, [])

  useEffect(() => {
    if (mintPrice === undefined) {
      getItemMintPrice()
    }
  }, [mintPrice])

  // extract keys from metadata rules keyname if any otherwise return key
  useEffect(() => {
    let emptyItemMetadata = {}
    Object.entries(metadata).forEach(([key, value]) => {
      if (!Object.prototype.hasOwnProperty.call(itemJson, key)) return
      if (value?.rules?.keyName) {
        emptyItemMetadata = {
          ...emptyItemMetadata,
          [value.rules.keyName]: (itemJson as { [key: string]: any })[key],
        }
      } else {
        emptyItemMetadata = {
          ...emptyItemMetadata,
          [key]: (itemJson as { [key: string]: any })[key],
        }
      }
    })
    if (Object.prototype.hasOwnProperty.call(itemJson, 'cropInfo')) {
      emptyItemMetadata = {
        ...emptyItemMetadata,
        cropInfo: (itemJson as { [key: string]: any }).cropInfo,
      }
    }
    // TODO: Check this because it is not being covered by the logic above. Maybe we should add display properties and behavior properties as a key of metadata.items
    if (Object.prototype.hasOwnProperty.call(itemJson, 'displayProperties')) {
      emptyItemMetadata = {
        ...emptyItemMetadata,
        displayProperties: (itemJson as { [key: string]: any }).displayProperties,
      }
    }
    if (emptyItemMetadata) {
      setItemMetadata(emptyItemMetadata as ItemMetadata)
    }
  }, [metadata])

  const sendImgMetadata = useCallback(
    async (bytes: Uint8Array, setError: (value: string | JSX.Element) => void) => {
      let retval: { cid: string; error: string | JSX.Element } = {
        cid: '',
        error: '',
      }
      try {
        setLoading(true)
        const response = await fetch(`${STUDIO_BOT}/image`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/octet-stream',
            'Access-Control-Request-Method': 'POST',
          },
          body: bytes,
        })
        // HTTP Error
        if (!response.ok) {
          setError(response.statusText)
          retval = {
            cid: '',
            error: response.statusText,
          }
          return retval
        }

        const serverRes = await response.json()
        // Server Error
        if (!serverRes.valid) {
          setError(serverRes.error)
          retval = {
            cid: '',
            error: serverRes.error,
          }
          return retval
        }
        // Success
        retval = {
          cid: serverRes.cid,
          error: '',
        }
      } catch (err: any) {
        if (err.message.includes('Failed to fetch')) {
          setError(errorMessages.studioBotConnection)
          retval = {
            cid: '',
            error: errorMessages.studioBotConnection,
          }
        } else {
          setError(err.message)
          retval = {
            cid: '',
            error: err.message,
          }
        }
      }
      setLoading(false)
      return retval
    },
    [itemMetadata]
  )

  const sendMetadata = useCallback(
    async (mtdata: ItemMetadata, setError: (value: string | JSX.Element) => void) => {
      let retval: { cid: string; error: string | JSX.Element } = {
        cid: '',
        error: '',
      }

      const jsonBody = JSON.stringify({
        metadata: JSON.stringify(mtdata),
      })
      try {
        setLoading(true)
        const serverRes = await post(`${STUDIO_BOT}/upload`, jsonBody)
        // Server Error
        if (!serverRes.valid) {
          setError(serverRes.error)
          retval = {
            cid: '',
            error: serverRes.error,
          }
          return retval
        }
        // Success
        retval = {
          cid: serverRes.cid,
          error: '',
        }
      } catch (err: any) {
        if (err.message.includes('Failed to fetch')) {
          setError(errorMessages.studioBotConnection)
          retval = {
            cid: '',
            error: errorMessages.studioBotConnection,
          }
        } else {
          setError(err.message)
          retval = {
            cid: '',
            error: err.message,
          }
        }
      }
      setLoading(false)
      return retval
    },
    [itemMetadata]
  )

  const submitItem = useCallback(
    async (retry = false) => {
      if (retry) {
        setErrorImageUpload('')
        setErrorMetadataUpload('')
        setErrorMintItem('')
      }
      if (!itemMetadata || !mintPrice) return
      // Convert image string to bytes
      const imgBytes = convertImageToBytes(previewImage)
      // Upload Image to IPFS
      const { cid: cidImage, error: errorImg } = await sendImgMetadata(
        imgBytes,
        setErrorImageUpload
      )
      if (!errorImg) {
        setActiveStep(1)
        // Update Item Metadata with Image CID
        const itemMetadataWithImage = {
          ...itemMetadata,
          image: `ipfs://${cidImage}`,
        }
        // Upload Metadata to IPFS
        const { cid: cidMetadata, error: errorMtd } = await sendMetadata(
          itemMetadataWithImage,
          setErrorMetadataUpload
        )
        if (!errorMtd) {
          setActiveStep(2)
          try {
            setLoading(true)
            await writeContract({
              address: contractAddress as `0x${string}`,
              abi: L1SpacesTemplate,
              functionName: 'mint',
              args: [cidMetadata],
              value: mintPrice.value,
            })
            setActiveStep(3)
            setLoading(false)
            deleteDraft()
            nextStep()
          } catch (err: any) {
            setLoading(false)
            if (err.message.includes('Failed to fetch')) {
              setErrorMintItem(errorMessages.studioBotConnection)
            } else {
              setErrorMintItem(err.message)
            }
          }
        }
      }
    },
    [itemJson, itemMetadata, mintPrice, writeContract]
  )

  const handleSaveDraft = useCallback(() => {
    navigate('/studio')
  }, [])

  return (
    <Stack>
      <Divider />
      <Title order={3}>{t('components.studio.checkout.title', 'Publish Your Item')}</Title>
      <Trans i18nKey="components.studio.checkout.description">
        <Text style={{ textAlign: 'left', color: theme.colors.paragraph[0] }}>
          Almost done. Confirm all your item information is correct in the preview panel. Then
          review the price of publishing this item. Note that content creation is currently in Beta
          on LAMINA1, and is only available to a select group of pre-funded early creators.
        </Text>
      </Trans>
      <Divider />
      <Stack
        style={{
          flexDirection: 'row',
          textAlign: 'left',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Stack
          style={{
            padding: '10px 0 10px 0',
            flexDirection: 'row',
            gap: '20px',
            alignItems: 'center',
          }}
        >
          <ImageCrop imageUrl={previewImage} data={itemJson.cropInfo} width={45} height={45} />
          <Stack style={{ gap: 0 }}>
            <Tooltip withArrow label={title} position="top-start" offset={10}>
              <Text style={{ fontFamily: 'monument-grotesk-bold', textAlign: 'left' }}>
                {truncateString(title, 30)}
              </Text>
            </Tooltip>
            <Text style={{ fontSize: '14px', color: theme.colors.textAndIcon[0] }}>1 Item</Text>
          </Stack>
        </Stack>
      </Stack>
      <Divider />
      <Stack
        style={{
          flexDirection: 'row',
          textAlign: 'left',
          alignItems: 'end',
          justifyContent: 'space-between',
        }}
      >
        <Stack style={{ gap: 1 }}>
          <Text style={{ fontSize: '14px', color: theme.colors.paragraph[0] }}>
            {/* Select a storage method */}
            {t('components.studio.checkout.storageMethod', 'Storage Method')}
          </Text>
          <Text
            style={{
              fontSize: '18px',
              color: theme.colors.paragraph[0],
              fontFamily: 'monument-grotesk-bold',
            }}
          >
            IPFS
          </Text>
        </Stack>
        {imageSize && (
          <Text
            style={{
              fontSize: '14px',
              color: theme.colors.paragraph[0],
              fontFamily: 'monument-grotesk-bold',
            }}
          >
            {convertBytesToUnit(imageSize + jsonSize)}
          </Text>
        )}
      </Stack>
      <TransactionSummary amount={mintPrice?.display} isOrder contractCalls={contractCalls} />
      {loading ? (
        <Stack
          style={{
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Stepper
            orientation="vertical"
            color={theme.colors.l1Primary[0]}
            iconSize={25}
            active={activeStep}
            progressIcon={<Loading size="20px" />}
            onStepClick={handleStepClick}
            allowNextStepsSelect={false}
            styles={{
              stepLabel: {
                fontFamily: 'monument-grotesk-bold',
              },
              stepBody: {
                marginLeft: '5px',
              },
              stepCompletedIcon: {
                color: theme.colors.background[0],
              },
              stepIcon: {
                border: `2px solid ${theme.colors.title[0]}`,
                backgroundColor: theme.colors.background[0],
                color: theme.colors.background[0],
              },
              verticalSeparator: {
                borderColor: theme.colors.l1Primary[0],
              },
            }}
          >
            <Stepper.Step icon={<></>} label="Step 1" description="Upload Item Image to IPFS" />
            <Stepper.Step icon={<></>} label="Step 2" description="Upload Item Metadata to IPFS" />
            <Stepper.Step icon={<></>} label="Step 3" description="Mint Item" />
          </Stepper>
        </Stack>
      ) : (
        <>
          <Trans i18nKey="components.studio.checkout.publishInfo">
            <Text style={{ textAlign: 'left', color: theme.colors.paragraph[0] }}>
              Pressing ‘Publish’ will bring this item on-chain to the LAMINA1 network, and publish
              it to your public Profile and Items tab.
            </Text>
          </Trans>
          {totalPrice > parseFloat(balance.fullValue) && (
            <Stack style={{ flexDirection: 'row', justifyContent: 'end' }}>
              <Text
                style={{ fontSize: '10pt', color: theme.colors.notification[0], textAlign: 'end' }}
              >
                {t(
                  'components.studio.checkout.insufficientBalance',
                  'Insufficient balance. You do not have enough L1 to publish this item.'
                )}
              </Text>
            </Stack>
          )}
          <Stack style={{ flexDirection: 'row', justifyContent: 'end', gap: '8px' }}>
            <Text
              style={{
                fontSize: '10pt',
                fontFamily: 'monument-grotesk-bold',
                color:
                  totalPrice > parseFloat(balance.fullValue) ? theme.colors.notification[0] : '',
              }}
            >
              {`${parseFloat(balance.fullValue).toFixed(4)} ${NATIVE_CHAIN_TICKER}`}
            </Text>
            <Text
              style={{
                fontSize: '10pt',
                color:
                  totalPrice > parseFloat(balance.fullValue) ? theme.colors.notification[0] : '',
              }}
            >
              {t('components.send.availableCheckout', 'Available')}
            </Text>
          </Stack>
          <Stack
            style={{
              flexDirection: 'row',
              gap: '10px',
              alignItems: 'center',
              justifyContent: 'end',
            }}
          >
            <RootButton onClick={handleSaveDraft}>
              {t('buttons.saveDraft', 'Save Draft')}
            </RootButton>
            <RootButton secondary onClick={prevStep}>
              {t('buttons.previous', 'Previous')}
            </RootButton>
            {!!errorImageUpload || !!errorMetadataUpload || !!errorMintItem ? (
              <RootButton
                onClick={() => submitItem(true)}
                disabled={totalPrice > parseFloat(balance.fullValue)}
              >
                {t('buttons.retry', 'Retry')}
              </RootButton>
            ) : (
              <RootButton
                onClick={submitItem}
                disabled={totalPrice > parseFloat(balance.fullValue) || loadingPrice}
              >
                {t('buttons.publish', 'Publish')}
              </RootButton>
            )}
          </Stack>
        </>
      )}
      {errorImageUpload && (
        <ErrorMessage
          message={t(
            'components.studio.checkout.errorImageUpload',
            'Error uploading image to IPFS'
          )}
          details={errorImageUpload}
        />
      )}
      {errorMetadataUpload && (
        <ErrorMessage
          message={t(
            'components.studio.checkout.errorMetadataUpload',
            'Error uploading metadata to IPFS'
          )}
          details={errorMetadataUpload}
        />
      )}
      {errorMintItem && (
        <ErrorMessage
          message={t('components.studio.checkout.errorMintItem', 'Error minting item')}
          details={errorMintItem}
        />
      )}
    </Stack>
  )
}
export default Checkout
